import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { Button, IconArrowLeft, IconChevronRight, TextField } from "@wilco/ui";
import { useCallback } from "react";
import { t } from "../i18n.ts";
import { employeesQuery } from "./queries.ts";
import { addDays, formatDay } from "./time.ts";

/** Имя сотрудника по id: ответы посещаемости, дверей и терминалов несут только id. */
export function useNames(): (id: string | null | undefined) => string {
  const employees = useQuery(employeesQuery);
  const data = employees.data;
  return useCallback(
    (id) => {
      if (!id) return t("people.unknownEmployee");
      return data?.find((e) => e.id === id)?.name ?? id;
    },
    [data],
  );
}

/** Активные сотрудники по имени для выбора в формах. */
export function useActiveEmployees() {
  const employees = useQuery(employeesQuery);
  return (employees.data ?? []).filter((e) => !e.archived).sort((a, b) => a.name.localeCompare(b.name, "uk"));
}

/**
 * Выбор дня: поле даты и соседние дни кнопками. Кнопки по 44 px, поле
 * сжимается: ряд не переносится и не шире экрана (docs/ui-rules.md, «2»).
 */
export function DayField({ value, onChange }: { value: string; onChange: (day: string) => void }) {
  return (
    <div className="people-day">
      <Button variant="ghost" size="icon" aria-label={t("people.day.prev")} onClick={() => onChange(addDays(value, -1))}>
        <IconArrowLeft />
      </Button>
      <TextField
        label={t("people.dayOf", { day: formatDay(value) })}
        type="date"
        value={value}
        onChange={(e) => {
          if (e.target.value) onChange(e.target.value);
        }}
        className="people-day-field"
      />
      <Button variant="ghost" size="icon" aria-label={t("people.day.next")} onClick={() => onChange(addDays(value, 1))}>
        <IconChevronRight />
      </Button>
    </div>
  );
}

/** Назад в «Кадри»: экраны раздела открываются из него. */
export function BackToHr() {
  return (
    <Button asChild variant="ghost" size="icon">
      <Link to="/hr" aria-label={t("people.back")}>
        <IconArrowLeft />
      </Link>
    </Button>
  );
}
